/**
 * Settings store with localStorage persistence
 * Manages theme, typography, and other user preferences
 */

import { writable, get } from 'svelte/store';

export type Theme = 'auto' | 'light' | 'dark';
export type FontFamily = 'sans-serif' | 'serif' | 'monospace';

export interface Settings {
  theme: Theme;
  fontSize: number;
  fontFamily: FontFamily;
  lineHeight: number;
  textWidth: number; // max-width in pixels
}

const defaultSettings: Settings = {
  theme: 'auto',
  fontSize: 16,
  fontFamily: 'sans-serif',
  lineHeight: 1.6,
  textWidth: 800,
};

const STORAGE_KEY = 'legere-settings';

/**
 * Load settings from localStorage
 */
function loadSettings(): Settings {
  if (typeof localStorage === 'undefined') {
    return { ...defaultSettings };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { ...defaultSettings };
    }

    const parsed = JSON.parse(stored);
    return {
      ...defaultSettings,
      ...parsed,
    };
  } catch (error) {
    console.error('Failed to load settings from localStorage:', error);
    return { ...defaultSettings };
  }
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings: Settings): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings to localStorage:', error);
  }
}

/**
 * Create settings store with persistence
 */
function createSettingsStore() {
  const { subscribe, set, update } = writable<Settings>(loadSettings());

  return {
    subscribe,

    /**
     * Update settings and persist to localStorage
     */
    update: (updater: (value: Settings) => Settings) => {
      update((current) => {
        const updated = updater(current);
        saveSettings(updated);
        return updated;
      });
    },

    /**
     * Set settings and persist to localStorage
     */
    set: (value: Settings) => {
      saveSettings(value);
      set(value);
    },

    /**
     * Update theme
     */
    setTheme: (theme: Theme) => {
      update((current) => {
        const updated = { ...current, theme };
        saveSettings(updated);
        applyTheme(theme);
        return updated;
      });
    },

    /**
     * Update font size
     */
    setFontSize: (fontSize: number) => {
      update((current) => {
        const updated = { ...current, fontSize };
        saveSettings(updated);
        applyTypography(updated);
        return updated;
      });
    },

    /**
     * Update font family
     */
    setFontFamily: (fontFamily: FontFamily) => {
      update((current) => {
        const updated = { ...current, fontFamily };
        saveSettings(updated);
        applyTypography(updated);
        return updated;
      });
    },

    /**
     * Update line height
     */
    setLineHeight: (lineHeight: number) => {
      update((current) => {
        const updated = { ...current, lineHeight };
        saveSettings(updated);
        applyTypography(updated);
        return updated;
      });
    },

    /**
     * Update text width
     */
    setTextWidth: (textWidth: number) => {
      update((current) => {
        const updated = { ...current, textWidth };
        saveSettings(updated);
        applyTypography(updated);
        return updated;
      });
    },

    /**
     * Reset to defaults
     */
    reset: () => {
      const defaults = { ...defaultSettings };
      saveSettings(defaults);
      set(defaults);
      applyTheme(defaults.theme);
      applyTypography(defaults);
    },

    /**
     * Initialize settings (apply theme and typography)
     */
    initialize: () => {
      const current = get({ subscribe });
      applyTheme(current.theme);
      applyTypography(current);
    },
  };
}

/**
 * Apply theme to document
 */
function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;

  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // Auto - use system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
}

/**
 * Apply typography settings to document
 */
function applyTypography(settings: Settings): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.style.setProperty('--reader-font-size', `${settings.fontSize}px`);
  root.style.setProperty('--reader-font-family', settings.fontFamily);
  root.style.setProperty('--reader-line-height', settings.lineHeight.toString());
  root.style.setProperty('--reader-text-width', `${settings.textWidth}px`);
}

export const settings = createSettingsStore();
